import { AxiosError } from 'axios';

import { Card, Typography, Stack } from '@cast/design-system';
import { convertUnit } from '@cast/utils';

import { CurrentUsageCard } from './CurrentUsageCard';

import { FailedToLoad } from '@cast/shared-messages-ui';

type StorageCurrentUsageProps = {
  usedBytes?: number;
  totalBytes?: number;
  iops?: number;
  provisionedIops?: number;
  throughputBytes?: number;
  provisionedThroughputBytes?: number;
  showCapacity?: boolean;
  isLoading?: boolean;
  error?: AxiosError | null;
  refetch?: () => void;
  isCloudDisksEnabled?: boolean;
};

export const StorageCurrentUsage = ({
  usedBytes,
  totalBytes,
  iops,
  provisionedIops,
  throughputBytes,
  provisionedThroughputBytes,
  showCapacity = true,
  isLoading,
  error,
  refetch,
  isCloudDisksEnabled,
}: StorageCurrentUsageProps) => {
  const usedCapacityGiB =
    typeof usedBytes === 'number'
      ? convertUnit(usedBytes, 'Bytes', 'GiB')
      : undefined;
  const provisionedCapacityGiB =
    typeof totalBytes === 'number'
      ? convertUnit(totalBytes, 'Bytes', 'GiB')
      : undefined;
  const usedThroughputMiBs =
    typeof throughputBytes === 'number'
      ? convertUnit(throughputBytes, 'Bytes', 'MiB')
      : undefined;
  const provisionedThroughputMiBs =
    typeof provisionedThroughputBytes === 'number'
      ? convertUnit(provisionedThroughputBytes, 'Bytes', 'MiB')
      : undefined;

  return (
    <Stack gap={8}>
      <Typography variant="A1" color="textSecondary">
        Current usage
      </Typography>
      {error ? (
        <Card
          sx={{
            p: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FailedToLoad title={error.message} refresh={refetch} />
        </Card>
      ) : (
        <Stack direction="row" gap={8} data-testid="current-usage-section">
          {showCapacity && (
            <CurrentUsageCard
              label="Capacity"
              testId="current-usage-capacity"
              used={usedCapacityGiB}
              provisioned={provisionedCapacityGiB}
              unit="GiB"
              isLoading={isLoading}
              isCloudDisksEnabled={isCloudDisksEnabled}
            />
          )}
          <CurrentUsageCard
            label="IOPS"
            testId="current-usage-iops"
            used={iops}
            provisioned={provisionedIops}
            unit="IOPS"
            isLoading={isLoading}
            isCloudDisksEnabled={isCloudDisksEnabled}
          />
          <CurrentUsageCard
            label="Throughput"
            testId="current-usage-throughput"
            used={usedThroughputMiBs}
            provisioned={provisionedThroughputMiBs}
            unit="MiB/s"
            isLoading={isLoading}
            isCloudDisksEnabled={isCloudDisksEnabled}
          />
        </Stack>
      )}
    </Stack>
  );
};
